import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bell, Check, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { onForegroundMessage } from "@/utils/pushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTIF_ICONS = {
  workout_reminder: "💪",
  streak_alert: "🔥",
  streak_warning: "🔥",
  hydration: "💧",
  badge_unlock: "🏆",
  weekly_summary: "📊",
  beast_mode: "⚡",
  recovery_suggestion: "🧘",
  test: "🏀",
  info: "📌",
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Live foreground push listener — refreshes bell immediately + shows toast preview
  useEffect(() => {
    let unsubscribe = null;
    (async () => {
      try {
        unsubscribe = await onForegroundMessage((payload) => {
          const title = payload?.notification?.title || "New notification";
          const body = payload?.notification?.body || "";
          // Toast preview
          toast.message(title, {
            description: body,
            duration: 6000,
            action: { label: "View", onClick: () => setOpen(true) },
          });
          // Refresh the bell list
          fetchNotifications();
        });
      } catch (e) {
        // Push not supported or initialized
      }
    })();
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API}/notifications/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      // Silently fail
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {}
  };

  const markRead = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/notifications/mark-read/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {}
  };

  const deleteNotif = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {}
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 text-zinc-400 hover:text-white transition-colors rounded-sm hover:bg-zinc-800"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-10 w-80 max-h-96 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <h3 className="text-sm font-bold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-orange-400 hover:text-orange-300 font-medium"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Latest push preview */}
          {notifications[0] && (
            <div
              className="px-4 py-3 bg-gradient-to-r from-orange-500/10 to-transparent border-b border-zinc-800"
              data-testid="latest-push-preview"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400 mb-1">
                Latest
              </p>
              <div className="flex items-start gap-2">
                <span className="text-xl mt-0.5">{NOTIF_ICONS[notifications[0].type] || "📌"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{notifications[0].title}</p>
                  <p className="text-xs text-zinc-400 line-clamp-2">{notifications[0].message}</p>
                  <p className="text-[10px] text-zinc-600 mt-0.5">{timeAgo(notifications[0].created_at)}</p>
                </div>
              </div>
            </div>
          )}

          {/* List */}
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No notifications yet</p>
                <p className="text-zinc-600 text-xs mt-1">You'll see workout reminders, badge unlocks, and more here</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`px-4 py-3 border-b border-zinc-800/50 hover:bg-zinc-800/50 transition-colors ${
                    !notif.read ? "bg-orange-500/5 border-l-2 border-l-orange-500" : ""
                  }`}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg mt-0.5">{NOTIF_ICONS[notif.type] || "📌"}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${!notif.read ? "text-white" : "text-zinc-300"}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-zinc-600 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {!notif.read && (
                        <button
                          onClick={() => markRead(notif.id)}
                          className="p-1 text-zinc-500 hover:text-green-400 transition-colors"
                          title="Mark read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotif(notif.id)}
                        className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
